import { Injectable } from '@angular/core';
import { CameraService } from './camera.service';
import { OrbitService } from './orbit.service';
import { StageService } from './stage.service';

declare const THREE: any;

/**
 * Touch + 3D: при стрелках ресайза пола/лестницы.
 *
 * Пока {@link Mobile3dResizeOrbitLockService.FROZEN_TOP_VIEW_CAMERA_LOCK} === `true`
 * (заморозка): только отключается вращение (OrbitControls), камера не двигается.
 *
 * При `false` — снова работает полный сценарий: вид сверху, сохранение дистанции до target,
 * плавный переход туда и обратно (см. приватные методы `_legacy*`).
 */
@Injectable()
export class Mobile3dResizeOrbitLockService {
  /**
   * `true` — перенос камеры вверх и анимации отключены, в коде остаются как «замороженные».
   * Поставьте `false`, чтобы снова включить полный режим.
   */
  static readonly FROZEN_TOP_VIEW_CAMERA_LOCK = true;

  private static readonly _CAMERA_TRANSITION_MS = 420;

  private _depth = 0;

  /** Режим заморозки: только запоминаем, был ли orbit включён. */
  private _orbitWasEnabled: boolean | null = null;

  /** Полный режим (незамороженный): сохранённая камера + target + флаг орбиты. */
  private _legacySaved: {
    camPos: any;
    camQuat: any;
    target: any;
    controlsEnabled: boolean;
  } | null = null;
  private _topViewAnimHandle: number | null = null;
  private _returnAnimHandle: number | null = null;
  private readonly _lerpTarget = new THREE.Vector3();

  constructor(
    private cameraManager: CameraService,
    private orbit: OrbitService,
    private stage: StageService
  ) {}

  private _shouldApply(): boolean {
    return (
      typeof navigator !== 'undefined' &&
      navigator.maxTouchPoints > 0 &&
      this.cameraManager.isIn3D() &&
      !!this.orbit.controls
    );
  }

  begin(): void {
    if (!this._shouldApply()) {
      return;
    }
    if (Mobile3dResizeOrbitLockService.FROZEN_TOP_VIEW_CAMERA_LOCK) {
      if (this._depth === 0) {
        this._orbitWasEnabled = this.orbit.controls.enabled;
        this.orbit.disable();
      }
      this._depth++;
      return;
    }

    this._legacyBegin();
    this._depth++;
  }

  end(): void {
    if (this._depth === 0) {
      return;
    }
    this._depth--;
    if (this._depth > 0) {
      return;
    }

    if (Mobile3dResizeOrbitLockService.FROZEN_TOP_VIEW_CAMERA_LOCK) {
      if (this._orbitWasEnabled === null || !this.orbit.controls) {
        return;
      }
      if (this._orbitWasEnabled) {
        this.orbit.enable();
      } else {
        this.orbit.disable();
      }
      this.orbit.controls.update();
      this._orbitWasEnabled = null;
      return;
    }

    this._legacyEnd();
  }

  // --- Замороженный полный режим (включается при FROZEN_TOP_VIEW_CAMERA_LOCK === false) ---

  private _legacyCancelTopViewAnim(): void {
    if (this._topViewAnimHandle !== null) {
      cancelAnimationFrame(this._topViewAnimHandle);
      this._topViewAnimHandle = null;
    }
  }

  private _legacyCancelReturnAnimFramesOnly(): void {
    if (this._returnAnimHandle !== null) {
      cancelAnimationFrame(this._returnAnimHandle);
      this._returnAnimHandle = null;
    }
  }

  private _legacySnapRestoreIfPending(): void {
    if (this._depth !== 0 || this._legacySaved === null) {
      return;
    }
    this._legacyCancelReturnAnimFramesOnly();
    this._legacyApplySavedCameraState(this._legacySaved);
    this._legacySaved = null;
  }

  private _legacyApplySavedCameraState(s: {
    camPos: any;
    camQuat: any;
    target: any;
    controlsEnabled: boolean;
  }): void {
    const cam = this.cameraManager.getPerspective();
    cam.position.copy(s.camPos);
    cam.quaternion.copy(s.camQuat);
    this.orbit.controls.target.copy(s.target);
    if (s.controlsEnabled) {
      this.orbit.enable();
    } else {
      this.orbit.disable();
    }
    this.orbit.controls.update();
  }

  private _legacyEaseOutCubic(t: number): number {
    const u = 1 - t;
    return 1 - u * u * u;
  }

  private _legacyBegin(): void {
    if (this._depth !== 0) {
      return;
    }
    this._legacySnapRestoreIfPending();
    const cam = this.cameraManager.getPerspective();
    this._legacyCancelTopViewAnim();
    this._legacySaved = {
      camPos: cam.position.clone(),
      camQuat: cam.quaternion.clone(),
      target: this.orbit.controls.target.clone(),
      controlsEnabled: this.orbit.controls.enabled
    };
    this.orbit.disable();

    const compound = this.stage.getCompoundFloor();
    const box = new THREE.Box3();
    if (compound) {
      box.setFromObject(compound);
    }
    const size = box.getSize(new THREE.Vector3());
    if (size.x < 1e-4 && size.y < 1e-4 && size.z < 1e-4) {
      box.set(new THREE.Vector3(-200, 0, -200), new THREE.Vector3(200, 0, 200));
    }
    const center = box.getCenter(new THREE.Vector3());
    const toTarget = new THREE.Vector3(center.x, center.y, center.z);
    const startPos = this._legacySaved.camPos.clone();
    const fromTarget = this._legacySaved.target.clone();
    let dist = startPos.distanceTo(fromTarget);
    dist = Math.min(this.orbit.maxDistance, Math.max(this.orbit.minDistance, dist));
    const toPos = new THREE.Vector3(toTarget.x, toTarget.y + dist, toTarget.z);

    const duration = Mobile3dResizeOrbitLockService._CAMERA_TRANSITION_MS;
    const t0 = typeof performance !== 'undefined' ? performance.now() : Date.now();

    const tick = (now: number) => {
      if (!this._legacySaved) {
        this._topViewAnimHandle = null;
        return;
      }
      const elapsed = now - t0;
      const linearT = Math.min(1, elapsed / duration);
      const t = this._legacyEaseOutCubic(linearT);

      cam.position.copy(startPos).lerp(toPos, t);
      this._lerpTarget.copy(fromTarget).lerp(toTarget, t);

      cam.up.set(0, 1, 0);
      cam.lookAt(this._lerpTarget);
      this.orbit.controls.target.copy(this._lerpTarget);
      this.orbit.controls.update();

      if (linearT < 1) {
        this._topViewAnimHandle = requestAnimationFrame(tick);
      } else {
        cam.position.copy(toPos);
        this.orbit.controls.target.copy(toTarget);
        cam.up.set(0, 1, 0);
        cam.lookAt(toTarget);
        this.orbit.controls.update();
        this._topViewAnimHandle = null;
      }
    };

    this._topViewAnimHandle = requestAnimationFrame(tick);
  }

  private _legacyEnd(): void {
    this._legacyCancelTopViewAnim();
    if (!this._legacySaved) {
      return;
    }

    const cam = this.cameraManager.getPerspective();
    const s = this._legacySaved;
    const fromPos = cam.position.clone();
    const fromTarget = this.orbit.controls.target.clone();
    const fromQuat = cam.quaternion.clone();

    const duration = Mobile3dResizeOrbitLockService._CAMERA_TRANSITION_MS;
    const t0 = typeof performance !== 'undefined' ? performance.now() : Date.now();

    const tick = (now: number) => {
      if (!this._legacySaved || this._legacySaved !== s) {
        this._returnAnimHandle = null;
        return;
      }
      const elapsed = now - t0;
      const linearT = Math.min(1, elapsed / duration);
      const t = this._legacyEaseOutCubic(linearT);

      cam.position.copy(fromPos).lerp(s.camPos, t);
      this._lerpTarget.copy(fromTarget).lerp(s.target, t);
      this.orbit.controls.target.copy(this._lerpTarget);
      cam.quaternion.copy(fromQuat).slerp(s.camQuat, t);
      this.orbit.controls.update();

      if (linearT < 1) {
        this._returnAnimHandle = requestAnimationFrame(tick);
      } else {
        this._legacyApplySavedCameraState(s);
        this._legacySaved = null;
        this._returnAnimHandle = null;
      }
    };

    this._returnAnimHandle = requestAnimationFrame(tick);
  }
}



// WEBPACK FOOTER //
// ./src/app/services/mobile-3d-resize-orbit-lock.service.ts